import { useState, useEffect } from 'react';
import { C, URG, STALE_CRITICAL, STALE_DEFAULT, RESUS_OPTIONS, calcNEWS2 } from '../constants';
import { AppShell, TopBar, BackBtn, WBtn } from '../components/Atoms';
import { hospDay } from '../utils';
import { calcGCS } from './VitalsTab';
import { NoteForm, NoteCard } from './NotesTab';

export default function WardRoundView({ pts, teams, subspecs = [], tf, initialTeam = 'all', initialSs = 'all', initialSvc = 'all', get24hChanges, startIdx = 0, onIdxChange, onClose, onOpenChart, onMarkReviewed, patchPt, shiftCode }) {
  // ── Filters ──────────────────────────────────────────────────────────────
  const [filterTeam, setFilterTeam]   = useState(initialTeam);
  const [filterSs,   setFilterSs]     = useState(initialSs);
  const [filterSvc,  setFilterSvc]    = useState(initialSvc);
  const [filterUrg,  setFilterUrg]    = useState('all');
  const [showFilter, setShowFilter]   = useState(false);

  // Unique services in pts
  const services = [...new Set(pts.filter(p => p.code).map(p => p.code))];
  // Urgencies present (excluding discharged/mortality)
  const URG_OPTS = ['critical','new','stable','mgh'];

  const sorted = [...pts]
    .filter(p => !['discharged','mortality'].includes(p.urgency))
    .filter(p => filterTeam === 'all' || p.team === filterTeam)
    .filter(p => filterSs   === 'all' || p.subspec === filterSs)
    .filter(p => filterSvc  === 'all' || p.code  === filterSvc)
    .filter(p => filterUrg  === 'all' || p.urgency === filterUrg)
    .sort((a, b) => {
      const ra = parseFloat((a.room || '').replace(/\D/g, '')) || 999;
      const rb = parseFloat((b.room || '').replace(/\D/g, '')) || 999;
      return ra - rb;
    });

  // ── Index management ─────────────────────────────────────────────────────
  const [idx, setIdxLocal] = useState(() => Math.min(startIdx, Math.max(sorted.length - 1, 0)));

  // Keep parent in sync
  useEffect(() => { onIdxChange && onIdxChange(idx); }, [idx]); // eslint-disable-line

  // Clamp idx if filters shrink the list
  const safeIdx = Math.min(idx, Math.max(sorted.length - 1, 0));

  const [showNoteForm, setShowNoteForm] = useState(false);
  useEffect(() => { setShowNoteForm(false); }, [safeIdx]); // eslint-disable-line

  if (sorted.length === 0) return (
    <AppShell>
      <TopBar left={<BackBtn onClick={onClose} />} title="Ward Round" right={
        <button onClick={() => setShowFilter(v => !v)}
          style={{ background: showFilter ? '#1A3D6A' : C.bg2, color: showFilter ? '#7DC4FF' : C.dim, border: '1px solid ' + (showFilter ? '#4A9EFF' : C.bd), borderRadius: 8, padding: '5px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
          ⚙ Filter
        </button>
      } />
      {showFilter && <FilterPanel teams={teams} subspecs={subspecs} services={services} filterTeam={filterTeam} setFilterTeam={setFilterTeam} filterSs={filterSs} setFilterSs={setFilterSs} filterSvc={filterSvc} setFilterSvc={setFilterSvc} filterUrg={filterUrg} setFilterUrg={setFilterUrg} URG_OPTS={URG_OPTS} />}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, color: C.dim }}>
        <div style={{ fontSize: 36 }}>🏥</div>
        <div style={{ fontSize: 14 }}>No patients match filters</div>
        <WBtn onClick={() => setShowFilter(true)}>Adjust Filters</WBtn>
        <WBtn onClick={onClose}>Done</WBtn>
      </div>
    </AppShell>
  );

  const p      = sorted[safeIdx];
  const u      = URG[p.urgency] || URG.stable;
  const pend   = (p.todos || []).filter(t => !t.done && t.status !== 'Done').length;
  const latest = [...(p.vitalsLog || [])].sort((a, b) => (b.date + b.time).localeCompare(a.date + a.time))[0];
  const staleThresh = p.urgency === 'critical' ? STALE_CRITICAL : STALE_DEFAULT;
  const lastReview  = p.lastReviewed ? new Date(p.lastReviewed) : null;
  const minsSince   = lastReview ? Math.floor((Date.now() - lastReview.getTime()) / 60000) : null;
  const reviewFresh = lastReview && (Date.now() - lastReview.getTime()) < staleThresh;
  const ch24        = get24hChanges ? get24hChanges(p) : [];
  const news2       = calcNEWS2(latest);
  const gcs         = calcGCS(latest);
  const resusOpt    = RESUS_OPTIONS.find(r => r.k === (p.resus || 'full'));

  const goNext = () => {
    onMarkReviewed(p.id);
    if (safeIdx < sorted.length - 1) setIdxLocal(safeIdx + 1);
    else { onIdxChange && onIdxChange(0); onClose(); }
  };
  const goPrev = () => { if (safeIdx > 0) setIdxLocal(safeIdx - 1); };
  const pct = Math.round((safeIdx / sorted.length) * 100);

  // Active filter count for badge
  const activeFilters = (filterTeam !== 'all' ? 1 : 0) + (filterSs !== 'all' ? 1 : 0) + (filterSvc !== 'all' ? 1 : 0) + (filterUrg !== 'all' ? 1 : 0);

  return (
    <AppShell>
      <TopBar
        left={<BackBtn onClick={onClose} />}
        title={'Round: ' + (safeIdx + 1) + ' / ' + sorted.length}
        right={
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => setShowFilter(v => !v)}
              style={{ background: showFilter || activeFilters > 0 ? '#1A3D6A' : C.bg2, color: showFilter || activeFilters > 0 ? '#7DC4FF' : C.dim, border: '1px solid ' + (activeFilters > 0 ? '#4A9EFF' : C.bd), borderRadius: 8, padding: '5px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
              ⚙ Filter{activeFilters > 0 ? ` (${activeFilters})` : ''}
            </button>
            <button onClick={() => { onMarkReviewed(p.id); onOpenChart(p.id, safeIdx); }}
              style={{ background: '#0A1E2E', border: '1px solid #1A3D6A', borderRadius: 8, color: '#4A9EFF', fontSize: 11, padding: '5px 10px', cursor: 'pointer', fontWeight: 700 }}>
              Open Chart
            </button>
          </div>
        }
      />

      {/* Filter panel */}
      {showFilter && (
        <FilterPanel
          teams={teams} subspecs={subspecs} services={services}
          filterTeam={filterTeam} setFilterTeam={v => { setFilterTeam(v); setIdxLocal(0); }}
          filterSs={filterSs}     setFilterSs={v   => { setFilterSs(v);   setIdxLocal(0); }}
          filterSvc={filterSvc}   setFilterSvc={v  => { setFilterSvc(v);  setIdxLocal(0); }}
          filterUrg={filterUrg}   setFilterUrg={v  => { setFilterUrg(v);  setIdxLocal(0); }}
          URG_OPTS={URG_OPTS}
        />
      )}

      {/* Progress bar */}
      <div style={{ height: 3, background: C.bg2, flexShrink: 0 }}>
        <div style={{ height: '100%', width: pct + '%', background: '#4A9EFF', transition: 'width 0.3s' }} />
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px 24px' }}>

        {/* Patient header */}
        <div style={{ background: C.bg3, border: '1px solid ' + C.bd, borderLeft: '4px solid ' + u.color, borderRadius: 12, padding: '14px 16px', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 20, fontWeight: 800, color: '#E0EEFA', flex: 1 }}>{p.name || 'Unnamed'}</span>
            {resusOpt && resusOpt.k !== 'full' && <span style={{ fontSize: 9, fontWeight: 900, color: resusOpt.color, background: resusOpt.bg, border: '1px solid ' + resusOpt.color + '66', borderRadius: 5, padding: '2px 7px' }}>{resusOpt.short}</span>}
            <span style={{ fontSize: 12, fontWeight: 700, color: u.color, background: u.bg, border: '1px solid ' + u.color + '44', borderRadius: 6, padding: '3px 10px' }}>{u.label}</span>
            {gcs && <span style={{ fontSize: 11, fontWeight: 800, color: gcs.color, background: gcs.color + '22', border: '1px solid ' + gcs.color + '44', borderRadius: 6, padding: '3px 8px' }} title={`E${gcs.e}V${gcs.v}M${gcs.m} · ${gcs.risk}`}>GCS {gcs.total}</span>}
            {news2 && <span style={{ fontSize: 11, fontWeight: 800, color: news2.color, background: news2.bg, border: '1px solid ' + news2.color + '44', borderRadius: 6, padding: '3px 8px' }} title={news2.reasons.join(', ')}>NEWS2 {news2.score}</span>}
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12, color: C.dim, marginBottom: 4 }}>
            <span>🛏 Rm {p.room || '?'}</span>
            <span>{p.age}{p.sex}</span>
            {p.weight && <span>{p.weight}{p.weightUnit || 'kg'}</span>}
            {p.admissionDate && <span style={{ color: '#A8C8A0', fontWeight: 700 }}>D{hospDay(p.admissionDate)}</span>}
            {p.consultant && <span>👤 {p.consultant}</span>}
          </div>
          <div style={{ fontSize: 13, color: '#6A9BBC', marginBottom: lastReview ? 6 : 0 }}>{p.diagnosis || 'No diagnosis'}</div>
          <div style={{ fontSize: 10, color: reviewFresh ? '#30D158' : '#FF4545', marginTop: 4 }}>
            {lastReview ? (reviewFresh ? '✓ ' : '⚠ ') + (minsSince < 60 ? minsSince + 'm ago' : Math.floor(minsSince / 60) + 'h ago') : '⚠ Not yet reviewed'}
          </div>
        </div>

        {/* ⚡ 24h Changes */}
        {ch24.length > 0 && (
          <div style={{ background: '#0A1E0A', border: '1px solid #1A3A1A', borderRadius: 10, padding: '10px 14px', marginBottom: 12 }}>
            <div style={{ fontSize: 9, fontWeight: 800, color: '#30D158', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>⚡ Last 24h</div>
            {ch24.map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 4 }}>
                <span style={{ fontSize: 10, fontWeight: 800, color: item.color, flexShrink: 0, minWidth: 72 }}>{item.label}</span>
                <span style={{ fontSize: 12, color: '#C8DFF0', lineHeight: 1.4 }}>{item.text}</span>
              </div>
            ))}
          </div>
        )}

        {/* Active issues */}
        {(p.issuesList || []).filter(i => !i.resolved).length > 0 && (
          <div style={{ background: C.bg3, border: '1px solid ' + C.bd, borderRadius: 10, padding: '10px 14px', marginBottom: 10 }}>
            <div style={{ fontSize: 9, fontWeight: 800, color: '#FF9500', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Active Issues</div>
            {(p.issuesList || []).filter(i => !i.resolved).map((issue, i) => (
              <div key={i} style={{ fontSize: 13, color: C.tx, padding: '3px 0', borderBottom: '1px solid ' + C.bg0 }}>
                {typeof issue === 'string' ? issue : (issue.text || '')}
              </div>
            ))}
            {!p.issuesList && p.activeIssues && <div style={{ fontSize: 13, color: C.tx, whiteSpace: 'pre-wrap' }}>{p.activeIssues}</div>}
          </div>
        )}

        {/* Latest vitals */}
        {latest && (
          <div style={{ background: C.bg3, border: '1px solid ' + C.bd, borderRadius: 10, padding: '10px 14px', marginBottom: 10 }}>
            <div style={{ fontSize: 9, fontWeight: 800, color: '#64D2FF', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Latest Vitals</div>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 13, color: C.tx }}>
              {latest.vBP && <span>BP <b>{latest.vBP}</b></span>}
              {latest.vHR && <span>HR <b>{latest.vHR}</b></span>}
              {latest.vRR && <span>RR <b>{latest.vRR}</b></span>}
              {latest.vT  && <span>T <b>{latest.vT}</b></span>}
              {latest.vO2 && <span>SpO₂ <b>{latest.vO2}%</b></span>}
              {latest.vAVPU && latest.vAVPU !== 'A' && <span>AVPU <b style={{ color: latest.vAVPU === 'V' ? '#FFD60A' : latest.vAVPU === 'P' ? '#FF9500' : '#FF4545' }}>{latest.vAVPU}</b></span>}
              {gcs && <span>GCS <b style={{ color: gcs.color }}>{gcs.total} E{gcs.e}V{gcs.v}M{gcs.m}</b></span>}
            </div>
          </div>
        )}

        {/* Pending tasks */}
        {pend > 0 && (
          <div style={{ background: C.bg3, border: '1px solid #FF950044', borderRadius: 10, padding: '10px 14px', marginBottom: 10 }}>
            <div style={{ fontSize: 9, fontWeight: 800, color: '#FF9500', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Pending Tasks ({pend})</div>
            {(p.todos || []).filter(t => !t.done && t.status !== 'Done').slice(0, 5).map(t => (
              <div key={t.id} style={{ fontSize: 13, color: C.tx, padding: '3px 0', borderBottom: '1px solid ' + C.bg0 }}>
                {t.text || 'Untitled'}
              </div>
            ))}
            {pend > 5 && <div style={{ fontSize: 11, color: C.dim2, marginTop: 4 }}>+{pend - 5} more</div>}
          </div>
        )}

        {/* Progress Notes */}
        <div style={{ background: C.bg3, border: '1px solid #2A4A6A', borderRadius: 10, padding: '10px 14px', marginBottom: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: showNoteForm ? 12 : (p.soapNotes?.length ? 8 : 0) }}>
            <div style={{ fontSize: 9, fontWeight: 800, color: '#7DC4FF', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              Progress Notes{p.soapNotes?.length ? ` · ${p.soapNotes.length}` : ''}
            </div>
            {!showNoteForm && (
              <button onClick={() => setShowNoteForm(true)} style={{ background: '#1A3D6A', border: 'none', color: '#7DC4FF', borderRadius: 7, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                ＋ Add Note
              </button>
            )}
          </div>

          {showNoteForm && patchPt && (
            <NoteForm
              cur={p}
              compact={true}
              onCancel={() => setShowNoteForm(false)}
              onSave={(note) => {
                patchPt(p.id, pt => ({ ...pt, soapNotes: [...(pt.soapNotes || []), note] }));
                setShowNoteForm(false);
              }}
            />
          )}

          {/* Latest note preview (collapsed) */}
          {!showNoteForm && (p.soapNotes || []).length === 0 && (
            <div style={{ fontSize: 12, color: C.dim2, fontStyle: 'italic' }}>No notes yet</div>
          )}
          {!showNoteForm && [...(p.soapNotes || [])].sort((a, b) => (b.date + b.time).localeCompare(a.date + a.time)).slice(0, 2).map((note, i) => (
            <NoteCard key={note.id} note={note} defaultExpanded={i === 0 && (p.soapNotes || []).length === 1} />
          ))}
          {!showNoteForm && (p.soapNotes || []).length > 2 && (
            <div style={{ fontSize: 11, color: C.dim, textAlign: 'center', marginTop: 4 }}>
              +{(p.soapNotes || []).length - 2} older note{(p.soapNotes || []).length - 2 !== 1 ? 's' : ''} — open chart to view all
            </div>
          )}
        </div>

        {/* Legacy note field */}
        {p.note && (
          <div style={{ background: C.bg3, border: '1px solid ' + C.bd, borderRadius: 10, padding: '10px 14px', marginBottom: 10 }}>
            <div style={{ fontSize: 9, fontWeight: 800, color: C.dim, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6 }}>Note</div>
            <div style={{ fontSize: 12, color: C.dim, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{p.note}</div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div style={{ padding: '12px 16px', borderTop: '1px solid ' + C.bd, background: C.bg0, flexShrink: 0, display: 'flex', gap: 10 }}>
        <button onClick={goPrev} disabled={safeIdx === 0}
          style={{ padding: '12px 20px', borderRadius: 10, background: safeIdx === 0 ? C.bg2 : '#0A1E2E', color: safeIdx === 0 ? C.dim2 : '#4A9EFF', border: '1px solid ' + (safeIdx === 0 ? C.bd : '#4A9EFF44'), cursor: safeIdx === 0 ? 'default' : 'pointer', fontSize: 13, fontWeight: 700 }}>
          ← Back
        </button>
        <button onClick={goNext}
          style={{ flex: 1, padding: '12px 0', borderRadius: 10, background: safeIdx === sorted.length - 1 ? '#081A0C' : '#1A3D6A', color: safeIdx === sorted.length - 1 ? '#30D158' : '#7DC4FF', border: '1px solid ' + (safeIdx === sorted.length - 1 ? '#30D15844' : '#4A9EFF44'), cursor: 'pointer', fontSize: 14, fontWeight: 800 }}>
          {safeIdx === sorted.length - 1 ? '✓ Finish Round' : 'Next Patient →'}
        </button>
      </div>
    </AppShell>
  );
}

// ── Filter panel ─────────────────────────────────────────────────────────────
function FilterPanel({ teams, subspecs = [], services, filterTeam, setFilterTeam, filterSs, setFilterSs, filterSvc, setFilterSvc, filterUrg, setFilterUrg, URG_OPTS }) {
  const URG_LABELS = { critical: { label: 'Critical', color: '#FF4545' }, new: { label: 'New', color: '#4A9EFF' }, stable: { label: 'Stable', color: '#30D158' }, mgh: { label: 'MGH', color: '#FF9500' } };
  const C2 = { bg2: '#1A1A2E', bg3: '#1C1C2E', bd: '#2A2A3E', dim: '#8E8E9A', dim2: '#555560', tx: '#E0EEFA' }; // eslint-disable-line no-unused-vars

  const pill = (active, color, onClick, label) => (
    <button onClick={onClick}
      style={{ padding: '4px 10px', borderRadius: 7, border: '1px solid ' + (active ? color : '#2A2A3E'), background: active ? color + '22' : '#1C1C2E', color: active ? color : '#8E8E9A', fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
      {label}
    </button>
  );

  return (
    <div style={{ background: '#121222', borderBottom: '1px solid #2A2A3E', padding: '10px 14px', flexShrink: 0 }}>
      {/* Team */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 9, fontWeight: 800, color: '#555560', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 5 }}>Team</div>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          {pill(filterTeam === 'all', '#C8DFF0', () => setFilterTeam('all'), 'All')}
          {teams.map(t => pill(filterTeam === t.id, t.color, () => setFilterTeam(t.id), t.name))}
        </div>
      </div>

      {/* Subspec */}
      {subspecs.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 9, fontWeight: 800, color: '#555560', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 5 }}>Subspec</div>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {pill(filterSs === 'all', '#C8DFF0', () => setFilterSs('all'), 'All')}
            {subspecs.map(s => pill(filterSs === s.id, s.color || '#A78BFA', () => setFilterSs(s.id), s.name))}
          </div>
        </div>
      )}

      {/* Urgency */}
      <div style={{ marginBottom: services.length > 0 ? 8 : 0 }}>
        <div style={{ fontSize: 9, fontWeight: 800, color: '#555560', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 5 }}>Status</div>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          {pill(filterUrg === 'all', '#C8DFF0', () => setFilterUrg('all'), 'All')}
          {URG_OPTS.map(k => { const u = URG_LABELS[k]; return u ? pill(filterUrg === k, u.color, () => setFilterUrg(k), u.label) : null; })}
        </div>
      </div>

      {/* Service */}
      {services.length > 0 && (
        <div>
          <div style={{ fontSize: 9, fontWeight: 800, color: '#555560', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 5 }}>Service</div>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {pill(filterSvc === 'all', '#C8DFF0', () => setFilterSvc('all'), 'All')}
            {services.map(s => pill(filterSvc === s, '#7DC4FF', () => setFilterSvc(s), s))}
          </div>
        </div>
      )}
    </div>
  );
}
